import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import {
  AlertProps as MuiAlertProps,
  Box,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Typography,
  useMediaQuery,
  Rating, // ⬅️ added
} from "@mui/material";
import theme from "../../elements/Theme";
import emailjs from "@emailjs/browser";
import OrangeHighlightButton from "../../elements/OrangeHighlightButton";
import AlertComponent from "../../elements/AlertComponent";
import LoadingComponent from "../../elements/Loading";

interface EnrollFormFrameProps {
  formRef?: React.RefObject<HTMLFormElement | null>;
}

const ReviewsV2FormFrame: React.FC<EnrollFormFrameProps> = ({ formRef }) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const reCaptchakey = "6LfHi2srAAAAAF3fQOjlrQDOXI2RtBHxQ2dUvr9L";
  // translations
  const { t, i18n } = useTranslation();
  let currentLanguage = i18n.language;
  // theme
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ⭐ rating (separate state so we don't change your existing formValues shape)
  const [rating, setRating] = useState<number | null>(null);

  // data
  const [formValues, setFormValues] = useState({
    fullName: "",
    description: "",
    childBirthday: "",
    parentName: "",
    phone: "",
    email: "",
    comments: "",
    educationLevel: "",
    rating: "",
  });

  // utils
  const [isLoading, setIsLoading] = useState(false);
  const [isAlert, setIsAlert] = useState({
    open: false,
    severity: "" as MuiAlertProps["severity"],
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // reCAPTCHA
    if (!recaptchaToken) {
      setIsAlert({
        open: true,
        severity: "error",
        message: t("EnrollFormFrame.Please verify that you're not a robot."),
      });
      return;
    }

    setIsLoading(true);
    const form = event.currentTarget;
    try {
      // emailjs
      emailjs.init("0VR9T4-49oj__IUhF");
      await emailjs.sendForm(
        "service_xg8pn69",
        "template_l1gjrmc",
        form,
        "0VR9T4-49oj__IUhF"
      );

      // push dataLayer GTM
      try {
        if (typeof window !== "undefined") {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: "form_submit" });
        }
      } catch (err) {
        console.warn("GTM dataLayer push failed:", err);
      }

      // success
      setIsAlert({
        open: true,
        severity: "success",
        message: t("EnrollFormFrame.Thank you for your submission!"),
      });
      setIsSubmitted(true);
      setFormValues({
        fullName: "",
        description: "",
        childBirthday: "",
        parentName: "",
        phone: "",
        email: "",
        comments: "",
        educationLevel: "",
        rating: ""
      });
      setRating(null); // ⬅️ reset stars
    } catch (error) {
      console.error("Error sending email:", error);
      setIsAlert({
        open: true,
        severity: "error",
        message: t(
          "EnrollFormFrame.Failed to send email. Please try again later."
        ),
      });
    } finally {
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        ref={formRef}
        px={isMobile ? "10px" : "73px"}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          // mt: isMobile ? "10vh" : "10vh",
          mb: isMobile ? "5vh" : "10vh",
        }}
      >

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: theme.palette.primary.main,
            p: 4,
            borderRadius: "25px",
            boxShadow: "4px 4px 6px 0px rgba(0, 0, 0, 0.5)",
            width: { sm: "100%", xs: "100%" },
            "& .MuiTextField-root": {
              margin: "8px 0",
              "&.Mui-focused": { color: "black" },
            },
            "& .MuiButton-root": { marginTop: "16px" },
            "& .MuiFormControlLabel-root": { display: "block", margin: "8px 0" },
            "& .MuiRadio-root": {
              color: theme.palette.secondary.main,
              "&.Mui-checked": { color: theme.palette.secondary.main },
              "&.Mui-focused": { color: "black" },
            },
            "& .MuiTypography-root": { color: theme.palette.text.primary },
            "& .MuiInputBase-input": { color: theme.palette.text.primary },
            "& .MuiFormLabel-root": {
              color: theme.palette.text.primary,
              "&.Mui-focused": { color: theme.palette.text.primary },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main,
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main,
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              { borderColor: theme.palette.secondary.main },
          }}
        >

          <Box sx={{ color: theme.palette.text.secondary }}>
            <Typography
              variant={"h3"}
              fontWeight={600}
              sx={{ "&&": { mb: isMobile ? "2vh" : "5vh", paddingTop: "4%", color: theme.palette.text.secondary } }}
              textAlign="left"
              color="inherit"
              maxWidth={800}
            >
              {t("ReviewsFormFrame.title")}
            </Typography>
          </Box>

          {/* ⭐ Rating input */}
          <FormControl sx={{ mt: 1, mb: 1 }}>
            <FormLabel sx={{ color: theme.palette.text.primary }}>
              {t("ReviewsFormFrame.rating", { defaultValue: "Your rating" })}
            </FormLabel>
            <Rating
              name="rating"               // included in form submission
              value={rating}
              precision={0.5}
              size="large"
              onChange={(_, value) => setRating(value)}
              sx={{ mt: 0.5 }}
              getLabelText={(v) => `${v} Star${v !== 1 ? "s" : ""}`}
            />
          </FormControl>

          <TextField
            fullWidth
            name="fullName"
            label={t("ReviewsFormFrame.Your full name")}
            variant="outlined"
            value={formValues.fullName}
            onChange={handleInputChange}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            name="description"
            label={t("ReviewsFormFrame.description")}
            variant="outlined"
            value={formValues.description}
            onChange={handleInputChange}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <br />
            {reCaptchakey && (
              <ReCAPTCHA
                key={currentLanguage}
                ref={recaptchaRef}
                sitekey={reCaptchakey}
                hl={currentLanguage}
                onChange={(token) => setRecaptchaToken(token)}
              />
            )}
            <br />
            {!isSubmitted ? (
              <OrangeHighlightButton type="submit">
                <Typography variant="h5" sx={{ color: "white !important" }}>
                  {t("EnrollFormFrame.SUBMIT")}
                </Typography>
              </OrangeHighlightButton>
            ) : (
              <Typography variant={"h3"}>
                {t("EnrollFormFrame.Thank you for your submission!")}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {isAlert.open && (
        <AlertComponent isAlert={isAlert} setIsAlert={setIsAlert} />
      )}

      <LoadingComponent loading={isLoading} />
    </>
  );
};

export default ReviewsV2FormFrame;
